import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/authContext';
import { getDraftRecipe, saveDraftToStack } from '../services/api';
import { PageHeading, Heading, Text, Section, PlainTable } from '../components/ui';

const DraftViewer = () => {
  const { id } = useParams();
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const { user } = useAuth();
  const navigate = useNavigate();

  // Load draft recipe
  useEffect(() => {
    const loadDraft = async () => {
      try {
        setLoading(true);
        const token = await user.getIdToken();
        
        // Use the API function instead of direct fetch
        const draftData = await getDraftRecipe(id, token);
        setDraft(draftData);
      } catch (err) {
        console.error('Error loading draft:', err);
        setError(err.message || 'Failed to load draft');
      } finally {
        setLoading(false);
      }
    };

    if (user && id) {
      loadDraft();
    }
  }, [user, id]);

  // Save draft to stack
  const handleSaveToStack = async () => {
    try {
      setSaving(true);
      setError('');

      const token = await user.getIdToken();
      
      // Use the API function instead of direct fetch
      const result = await saveDraftToStack(id, token);
      
      // Redirect to the new stack entry
      navigate(`/stack/${result.stackId}`);

    } catch (err) {
      console.error('Error saving to stack:', err);
      setError(err.message || 'Failed to save to stack');
    } finally {
      setSaving(false);
    }
  };

  // Start live session
  const handleStartLiveSession = () => {
    navigate(`/live/${id}`);
  };

  // Format duration for display
  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return 'No timing';
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    if (minutes === 0) {
      return `${remainingSeconds}s`;
    } else if (remainingSeconds === 0) {
      return `${minutes}m`;
    } else {
      return `${minutes}m ${remainingSeconds}s`;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 px-0">
        <Section>
          <Text className="text-center">Loading draft recipe...</Text>
        </Section>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-neutral-50 px-0">
        <Section>
          <div className="bg-danger text-white px-4 py-2 hairline">
            {error}
          </div>
          <button
            onClick={() => navigate('/bases')}
            className="mt-4 px-4 py-2 bg-neutral-900 text-neutral-0 hairline font-mono uppercase tracking-wider text-sm hover:opacity-80"
          >
            Back to Bases
          </button>
        </Section>
      </div>
    );
  }

  if (!draft) {
    return (
      <div className="min-h-screen bg-neutral-50 px-0">
        <Section>
          <Text>Draft not found.</Text>
        </Section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 px-0">
      <div className="w-full py-16">
        {/* Hero Section */}
        <div className="bg-neutral-0 hairline px-0 py-8 mb-0">
          <Text size="sm" className="font-mono uppercase tracking-wider text-neutral-900 opacity-60 mb-2">
            DRAFT RECIPE • {draft.kernel_title}
          </Text>
          <PageHeading size="hero" className="text-neutral-900 mb-4">
            Generated Recipe
          </PageHeading>
          <Text className="text-neutral-900 mb-6">
            Created {new Date(draft.created_at).toLocaleDateString()}
          </Text>

          {/* Action Buttons */}
          <div className="flex space-x-4">
            <button
              onClick={handleSaveToStack}
              disabled={saving}
              className={`
                px-6 py-3 bg-neutral-900 text-neutral-0 hairline font-mono uppercase tracking-wider text-sm
                transition-opacity duration-120
                ${saving ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-80'}
              `}
            >
              {saving ? 'SAVING...' : 'SAVE TO STACK'}
            </button>
            
            <button
              onClick={handleStartLiveSession}
              className="px-6 py-3 bg-neutral-0 text-neutral-900 hairline font-mono uppercase tracking-wider text-sm hover:bg-neutral-50 transition-colors duration-120"
            >
              START LIVE SESSION
            </button>
          </div>
        </div>

        {/* User Inputs */}
        <Section>
          <Heading level={2} className="text-neutral-900">Your Inputs</Heading>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0">
            {Object.entries(draft.slots || {}).map(([key, value]) => (
              <div key={key} className="bg-neutral-50 hairline p-4">
                <Text size="sm" className="font-mono uppercase tracking-wider text-neutral-900 opacity-60 mb-1">
                  {key.replace(/([A-Z])/g, ' $1').toUpperCase()}
                </Text>
                <Text className="text-neutral-900">
                  {value}
                </Text>
              </div>
            ))}
          </div>
        </Section>

        {/* Recipe Steps */}
        <Section>
          <Heading level={2} className="text-neutral-900">Recipe Steps</Heading>
          <div className="bg-neutral-0 hairline">
            <PlainTable>
              <thead>
                <PlainTable.Row>
                  <PlainTable.Cell header>#</PlainTable.Cell>
                  <PlainTable.Cell header>Step</PlainTable.Cell>
                  <PlainTable.Cell header>Instructions</PlainTable.Cell>
                  <PlainTable.Cell header>Duration</PlainTable.Cell>
                </PlainTable.Row>
              </thead>
              <tbody>
                {draft.steps?.map((step, index) => (
                  <PlainTable.Row key={step.id}>
                    <PlainTable.Cell>
                      <Text size="sm" className="font-mono text-neutral-900">
                        {index + 1}
                      </Text>
                    </PlainTable.Cell>
                    <PlainTable.Cell>
                      <Text size="sm" className="font-medium text-neutral-900">
                        {step.title}
                      </Text>
                    </PlainTable.Cell>
                    <PlainTable.Cell>
                      <Text size="sm" className="text-neutral-900">
                        {step.body}
                      </Text>
                    </PlainTable.Cell>
                    <PlainTable.Cell>
                      <Text size="sm" className="font-mono text-neutral-900">
                        {formatDuration(step.duration_sec)}
                      </Text>
                    </PlainTable.Cell>
                  </PlainTable.Row>
                ))}
              </tbody>
            </PlainTable>
          </div>
        </Section>

        {error && (
          <Section>
            <div className="bg-danger text-white px-4 py-2 hairline">
              {error}
            </div>
          </Section>
        )}
      </div>
    </div>
  );
};

export default DraftViewer; 
